import { useEffect, useState } from "react";
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
} from "@chakra-ui/react";
import "./Autocomplete.css";

function AutocompleteInput({
  name,
  label,
  placeholder,
  apiIndex,
  fetchParams = {},
  selectedItem,
  setSelectedItem,
  itemLabel,
  itemValue = (item) => itemLabel(item),
  inputFilter = true,
  isRequired = false,
  isInvalid = false,
  errorText,
  onBlur,
  pattern,
}) {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const normalizeString = (str) =>
    str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const suggestionValues = suggestions?.map((suggestion) =>
    Object.values(suggestion).join(", ")
  );

  const handleInputChange = (e) => {
    if (selectedItem) {
      setSelectedItem(null);
    }

    const value = e.target.value;

    if (value.length < inputValue.length) {
      setInputValue(value);
      return;
    }

    if (!inputFilter) {
      setInputValue(value);
    } else {
      if (inputValue.length < 1) {
        setInputValue(value);
      } else {
        suggestionValues?.forEach((el) => {
          if (normalizeString(el).includes(normalizeString(value))) {
            setInputValue(value);
          } else {
            return;
          }
        });
      }
    }
  };

  const handleKeyDown = (e) => {
    if (suggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        setFocusedIndex((prevIndex) =>
          prevIndex < suggestions.length - 1 ? prevIndex + 1 : 0
        );
        break;
      case "ArrowUp":
        setFocusedIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : suggestions.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < suggestions.length) {
          const selectedSuggestion = suggestions[focusedIndex];
          onItemSelect(selectedSuggestion);
        }
        break;
      case "Escape":
        setInputValue("");
        setSuggestions([]);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const baseUrl = process.env.REACT_APP_SEARCH_API_BASE_URL;
    const apiKey = process.env.REACT_APP_SEARCH_API_KEY;

    if (inputValue.length >= 1) {
      const fetchSuggestions = async () => {
        try {
          const response = await fetch(`${baseUrl}/${apiIndex}/search`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "/*/",
              Authorization: `Bearer ${apiKey}`,
            },
            body: JSON.stringify({
              q: inputValue,
              ...fetchParams,
            }),
          });

          const data = await response.json();
          const { hits } = data;
          setSuggestions(hits || []);
        } catch (error) {
          console.error(error);
        }
      };

      fetchSuggestions();
    } else {
      return;
    }
  }, [inputValue, apiIndex, fetchParams]);

  const onItemSelect = (item) => {
    const label = itemLabel(item);

    setInputValue(label);
    setSelectedItem(item);
    setSuggestions([]);
  };

  return (
    <FormControl
      isRequired={isRequired}
      autoComplete="off"
      isInvalid={isInvalid}
    >
      <FormLabel htmlFor={name}>{label}</FormLabel>
      <Input
        type="text"
        id={`${name}_display`}
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        autoComplete="off"
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        pattern={pattern}
        required={isRequired && !selectedItem}
      />
      {isInvalid && <FormErrorMessage>{errorText}</FormErrorMessage>}
      <input
        type="hidden"
        name={name}
        value={selectedItem ? itemValue(selectedItem) : ""}
        required={isRequired}
      />
      {suggestions.length > 0 && inputValue && selectedItem === null && (
        <ul className="suggestions-list">
          {suggestions.map((suggestion, index) => (
            <li
              key={index}
              className={`suggestion-item ${
                index === focusedIndex ? "focused" : ""
              }`}
              onClick={() => {
                onItemSelect(suggestion);
              }}
              onMouseEnter={() => setFocusedIndex(index)}
            >
              {itemLabel(suggestion)}
            </li>
          ))}
        </ul>
      )}
    </FormControl>
  );
}

export default AutocompleteInput;
